import React, { useEffect, useMemo, useState } from 'react';
import { Button, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import Paginator from '../../../../components/Systema/Data/Paginator';

const getColumnMinWidth = (col) => {
  if (col.minWidth) return col.minWidth;
  if (col.key === 'historico' || col.key === 'emenda_objeto') return 300;
  if (col.key && col.key.indexOf('nome_') === 0) return 240;
  return 150;
};

const isLongTextColumn = (key) => (
  [
    'emenda_objeto',
    'detalhamento_objeto',
    'historico',
    'resumo_licitacao',
    'nome_unidade_orcamentaria',
    'nome_despesa',
    'nome_acao',
    'nome_fonte_recurso',
    'receita_nome',
    'fonte_recurso_nome',
    'nome_fornecedor',
  ].indexOf(key) >= 0
);

const hasValue = (value) => value !== null && value !== undefined && value !== '';

export default function ReportTable({ report, loading, columnLabels = {}, onShowEmpenho }) {
  const [page, setPage] = useState(1);
  const limit = 50;
  const start = limit * (page - 1);

  useEffect(() => {
    setPage(1);
  }, [report]);

  const { header1, header2 } = useMemo(() => {
    const header1 = [];
    const header2 = [];

    if (report) {
      report.config.columns.forEach((col) => {
        if (!col.displayable) return;

        const name = columnLabels[col.key] || col.name;

        if (name.indexOf(' - ') >= 0) {
          let [group, column] = name.split(' - ');
          group = group.replace(/(\\-)/g, '-');
          column = column.replace(/(\\-)/g, '-');
          const previous = header1[header1.length - 1];

          if (previous && previous.name === group) {
            previous.colSpan += 1;
          } else {
            header1.push({ name: group, colSpan: 1, rowSpan: 1, minWidth: getColumnMinWidth(col) });
          }

          header2.push({ name: column, colSpan: 1, rowSpan: 1, minWidth: getColumnMinWidth(col) });
        } else {
          header1.push({ name: name.replace(/(\\-)/g, '-'), colSpan: 1, rowSpan: 2, minWidth: getColumnMinWidth(col) });
        }
      });
    }

    return { header1, header2 };
  }, [columnLabels, report]);

  return (
    <div className='emenda-report-table-container'>
      <Table className='table-bordered table-hover table-striped emenda-table'>
        <thead>
          <tr>
            {header1.map((col, index) => (
              <th key={`${col.name}-${index}`} colSpan={col.colSpan} rowSpan={col.rowSpan} className='align-middle' style={{ minWidth: col.minWidth }}>
                {col.name}
              </th>
            ))}
          </tr>
          <tr>
            {header2.map((col, index) => (
              <th key={`${col.name}-${index}`} colSpan={col.colSpan} rowSpan={col.rowSpan} className='align-middle' style={{ minWidth: col.minWidth }}>
                {col.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report &&
            report.data.slice(start, start + limit).map((row, index) => (
              <TableRow key={index} row={row} columns={report.config.columns} onShowEmpenho={onShowEmpenho} />
            ))}
          {!loading && (!report || report.data.length === 0) && (
            <tr>
              <td colSpan={Math.max(header1.length, 1)} className='text-center text-muted'>
                Nenhum lançamento encontrado.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Paginator loading={loading} page={page} setPage={setPage} lastPage={report ? Math.ceil(report.data.length / limit) : 1} />
    </div>
  );
}

function TableRow({ row, columns, onShowEmpenho }) {
  const id = row[columns.findIndex((col) => col.key === 'id')];

  return (
    <tr>
      {columns.map((col, index) => {
        if (!col.displayable) return null;

        const cell = row[index];

        if (col.key === 'empenho') {
          return (
            <td key={col.key} className='align-middle text-center'>
              <OverlayTrigger overlay={<Tooltip>Clique para ver mais detalhes</Tooltip>}>
                <Button variant='link' className='p-0' onClick={() => onShowEmpenho && onShowEmpenho(id)}>
                  {cell}
                </Button>
              </OverlayTrigger>
            </td>
          );
        }

        if (col.tipo === 'REAL') {
          return (
            <td key={col.key} className='text-right align-middle'>
              {hasValue(cell) ? cell.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '-'}
            </td>
          );
        }

        if (col.tipo === 'DATA') {
          return (
            <td key={col.key} className='text-center align-middle'>
              {cell ? new Date(cell).toLocaleDateString('pt-BR') : '-'}
            </td>
          );
        }

        return (
          <td key={col.key} className={`align-middle ${isLongTextColumn(col.key) ? 'emenda-table-text-wrap' : ''}`}>
            {hasValue(cell) ? cell : '-'}
          </td>
        );
      })}
    </tr>
  );
}
